import styles from './SettingsList.module.scss';
import { useTranslation } from 'react-i18next';
import packageJson from '../../../../../package.json';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PREMIUM_UPGRADE_PATH,
  shouldBlockBillingNavigation,
} from 'utils/premiumUpgradePath';
import { ROUTES } from '../../../../routes/constants';
import { authActions } from '../../../Auth/store';
import ConfirmDeleteModal from '../../../Common/ConfirmDeleteModal';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmModal from '../../../Common/ConfirmModal';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { settingsActions } from 'Containers/Settings/store';
import LanguageSelectorModal from 'Containers/Common/LanguageSelectorModal';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import { isDemoMode } from 'demo';
import {
  getCustomerProducts,
  getProductsLoading,
} from 'Containers/Settings/store/selectors';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import { ReactComponent as Exit } from '../../../../assets/svg/icons/esci.svg';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import { modalActions } from 'Containers/Modals/store';
import {
  ChevronRight,
  CreditCard,
  Flame,
  Headset,
  Languages,
  LogOut,
  // MessageCircle,
  Pencil,
  Share2,
  Shield,
  Trash2,
  Users,
  Wallet,
} from 'lucide-react';

const selectProducts = getCustomerProducts();
const selectProductsLoading = getProductsLoading();

const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

const SettingsList = () => {
  const { t } = useTranslation();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const activeSalon = useSelector(getActiveSalon());
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const iconSize = isMobile ? 24 : 18;
  const chevronSize = isMobile ? 22 : 18;
  const [searchParams, setSearchParams] = useSearchParams({
    help: 0,
    delete: 0,
    logout: 0,
  });

  const isPremiumUnlocked = useIsPremiumUnlocked();

  const salonEmail = useMemo(() => {
    try {
      const salon = localStorage.getItem('salons');
      const salonData = salon && JSON.parse(salon);
      return salonData?.[0]?.email;
    } catch {
      return undefined;
    }
  }, []);

  const goToUpgrade = () => {
    navigate(PREMIUM_UPGRADE_PATH);
  };

  const hideCommerce =
    isDemoMode(activeSalon) ||
    (salonEmail === 'demo@demo.it' && process.env.REACT_APP_PAYMENT_DEMO == 'true');

  const salonManagementCardData = useMemo(
    () =>
      [
        {
          key: 'editInfo',
          icon: Pencil,
          title: t('settings.settingsList.cards.editInfo.title'),
          description: t('settings.settingsList.cards.editInfo.description'),
          link: `${ROUTES.SETTINGS}${ROUTES.SALONE}`,
          newTab: false,
        },
        {
          key: 'staffAccounts',
          icon: Users,
          title: t('settings.settingsList.cards.staffAccounts.title'),
          description: t('settings.settingsList.cards.staffAccounts.description'),
          link: `${ROUTES.SETTINGS}${ROUTES.ACCOUNTS}`,
          newTab: false,
          premiumLocked: !isPremiumUnlocked,
        },
        {
          key: 'expenses',
          icon: Wallet,
          title: t('settings.settingsList.cards.expenses.title'),
          description: t('settings.settingsList.cards.expenses.description'),
          link: `${ROUTES.SETTINGS}${ROUTES.EXPENSES}`,
          newTab: false,
          premiumLocked: !isPremiumUnlocked,
        },
        {
          key: 'security',
          icon: Shield,
          title: t('settings.settingsList.cards.security.title'),
          description: t('settings.settingsList.cards.security.description'),
          link: `${ROUTES.SETTINGS}${ROUTES.SECURITY}`,
          newTab: false,
        },
        hideCommerce
          ? null
          : {
              key: 'billing',
              icon: CreditCard,
              title: t('settings.settingsList.cards.billing.title'),
              description: t('settings.settingsList.cards.billing.description'),
              link: `${ROUTES.SETTINGS}${ROUTES.PAYMENTS}`,
              newTab: false,
            },
        // hideCommerce
        //   ? null
        //   : {
        //       key: 'otp',
        //       icon: MessageCircle,
        //       title: t('settings.settingsList.cards.otp.title'),
        //       description: t('settings.settingsList.cards.otp.description'),
        //       link: `${ROUTES.SETTINGS}${ROUTES.OTP}`,
        //       newTab: false,
        //     },
        {
          key: 'sharing',
          icon: Share2,
          title: t('settings.settingsList.cards.sharing.title'),
          description: t('settings.settingsList.cards.sharing.description'),
          link: `${ROUTES.SETTINGS}${ROUTES.SHARING}`,
          newTab: false,
        },
        {
          key: 'support',
          icon: Headset,
          title: t('settings.settingsList.cards.support.title'),
          description: t('settings.settingsList.cards.support.description'),
          link: 'https://wa.me/393533357748',
          newTab: true,
        },
        {
          key: 'language',
          icon: Languages,
          title: t('settings.settingsList.cards.language.title'),
          description: t('settings.settingsList.cards.language.description'),
          action: () => setIsLanguageModalOpen(true),
          newTab: false,
        },
        {
          key: 'deleteAccount',
          icon: Trash2,
          title: t('settings.settingsList.cards.deleteAccount.title'),
          description: t('settings.settingsList.cards.deleteAccount.description'),
          link: `${ROUTES.SETTINGS}?delete=1`,
          newTab: false,
          danger: true,
          separator: true,
        },
        {
          key: 'logout',
          icon: LogOut,
          title: t('settings.settingsList.cards.logout.title'),
          description: t('settings.settingsList.cards.logout.description'),
          link: `${ROUTES.SETTINGS}?logout=1`,
          newTab: false,
          danger: true,
        },
      ].filter(Boolean),
    [t, isPremiumUnlocked, salonEmail, hideCommerce],
  );

  const products = useSelector(selectProducts);
  const productsLoading = useSelector(selectProductsLoading);
  const hasProducts = Boolean(products && Object.keys(products).length);

  useEffect(() => {
    if (hasProducts || productsLoading || localStorage.getItem('currentPrice')) return;
    dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch, hasProducts, productsLoading]);

  const handleItemClick = (item) => {
    if (item.premiumLocked) {
      goToUpgrade();
      return;
    }
    // Ghost tap dopo "Passa a Premium": non aprire Fatturazione.
    if (item.key === 'billing' && shouldBlockBillingNavigation(item.link)) {
      navigate(PREMIUM_UPGRADE_PATH);
      return;
    }
    if (item.newTab) openInNewTab(item.link);
    else if (item.action) item.action();
    else navigate(item.link);
  };

  return (
    <>
      <ConfirmDeleteModal
        isModalOpen={!!+searchParams.get('delete')}
        setIsModalOpen={setSearchParams}
        title={t('settings.settingsList.modals.deleteSalon.title')}
        buttons={[t('common.actions.cancel'), t('common.actions.disable')]}
        action={() => dispatch(authActions.DELETE_SALON.REQUEST({}, () => setSearchParams(false)))}
      />
      <ConfirmModal
        isModalOpen={!!+searchParams.get('logout')}
        setIsModalOpen={setSearchParams}
        title={t('settings.settingsList.modals.logout.title')}
        subtitle={t('settings.settingsList.modals.logout.subtitle')}
        Icon={Exit}
        variant="danger"
        cancelLabel={t('common.actions.cancel')}
        confirmLabel={t('common.actions.logout')}
        action={() =>
          dispatch(
            authActions.LOG_OUT.REQUEST({}, () => {
              window.location.href = ROUTES.SIGN_IN;
            }),
          )
        }
      />
      <div
        className={clx(styles.settings_list_container, {
          [styles.settings_list_container_mobile]: isMobile,
        })}
      >
        <div className={styles.header}>
          <TitleSectionBack
            title={t('settings.settingsList.title')}
            description={t('settings.settingsList.description')}
          />
        </div>

        <div className={styles.list_card}>
          {salonManagementCardData.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                className={clx(styles.list_item, {
                  [styles.list_item_danger]: item.danger,
                  [styles.list_item_separator]: item.separator,
                  [styles.list_item_premium]: item.premiumLocked,
                })}
                onClick={() => handleItemClick(item)}
              >
                <span className={styles.item_icon} aria-hidden="true">
                  <Icon size={iconSize} strokeWidth={2.2} />
                  {item.premiumLocked ? (
                    <span className={styles.item_premium_badge} aria-hidden="true">
                      <Flame size={11} strokeWidth={2.25} fill="currentColor" />
                    </span>
                  ) : null}
                </span>
                <span className={styles.item_copy}>
                  <span className={styles.item_title_row}>
                    <span className={styles.item_title}>{item.title}</span>
                    {item.premiumLocked ? (
                      <span className={styles.premium_chip}>
                        <Flame size={12} strokeWidth={2.25} fill="currentColor" />
                        {t('statistics.premiumLock.title')}
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.item_description}>{item.description}</span>
                </span>
                <ChevronRight size={chevronSize} strokeWidth={2.2} aria-hidden="true" />
              </button>
            );
          })}
        </div>
        <div className={styles.footer_version}>{packageJson.version}</div>
      </div>
      <LanguageSelectorModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        closeButton={true}
      />
    </>
  );
};

export default SettingsList;
